import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { getBranches } from "@/lib/api";

const BranchContext = createContext(null);
const STORAGE_KEY = "selected_branch";

// Single-Branch Standalone Mode: the app is locked to one shop.
// To re-enable multi-branch later, flip SINGLE_BRANCH_MODE to false — the
// branch switcher and branch-gate selection flow will return automatically.
export const SINGLE_BRANCH_MODE = true;
export const LOCKED_BRANCH_ID = "south-melbourne";

export function BranchProvider({ children }) {
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(() =>
    SINGLE_BRANCH_MODE ? LOCKED_BRANCH_ID : localStorage.getItem(STORAGE_KEY) || null
  );
  const [loading, setLoading] = useState(true);

  const loadBranches = useCallback(async () => {
    try {
      const data = await getBranches();
      setBranches(data);
      setSelectedBranchId((prev) => {
        if (SINGLE_BRANCH_MODE) return LOCKED_BRANCH_ID;
        if (prev && data.some((b) => b.id === prev)) return prev;
        // Auto-select when only one branch exists; otherwise force explicit choice
        if (data.length === 1) {
          localStorage.setItem(STORAGE_KEY, data[0].id);
          return data[0].id;
        }
        return prev && data.some((b) => b.id === prev) ? prev : null;
      });
    } catch {
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Deferred load past the synchronous tick (no setState during the effect body)
  useEffect(() => {
    let active = true;
    Promise.resolve().then(() => {
      if (active) loadBranches();
    });
    return () => {
      active = false;
    };
  }, [loadBranches]);

  const setBranch = useCallback((id) => {
    if (SINGLE_BRANCH_MODE) return; // locked to a single branch
    setSelectedBranchId(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const currentBranch = useMemo(
    () => branches.find((b) => b.id === selectedBranchId) || null,
    [branches, selectedBranchId]
  );

  const value = useMemo(
    () => ({ branches, selectedBranchId, setBranch, currentBranch, loading }),
    [branches, selectedBranchId, setBranch, currentBranch, loading]
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch() {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error("useBranch must be used within a BranchProvider");
  return ctx;
}
